import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TCreateRestaurantSelectedItem } from '../../../../../createRestaurantState/createRestaurantContext';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { IItemForGetShortInfo, IGetCustomersShort, EntityStatus } from 'types';
import { getCustomersShortInfo } from 'backend/customers';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import style from './CustomerSelect.module.scss';

interface ICustomerSelect {
	setIDCustomer: (value: TCreateRestaurantSelectedItem) => void;
	setFetchedCustomers: (value: IItemForGetShortInfo[]) => void;
}

const CustomerSelect: React.FC<ICustomerSelect> = ({ setIDCustomer, setFetchedCustomers }) => {
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(getCustomersShortInfo);
	const { t } = useTranslation('error');
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');

	useEffect(() => {
		response && setFetchedCustomers(response.data.customers);
	}, [response]);

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectCustomer') }], [t]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		const items = response ? response.data.customers : [];
		const visibleItems = showArchived ? items : items.filter((customer) => customer.status === EntityStatus.Active);

		return visibleItems.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [response, showArchived]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<FormSelect
			containerClassName={style.select}
			testId="CustomerSelect"
			name="IDSelectedCustomer"
			label="customerLabel"
			options={mappedOptions}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			onChange={setIDCustomer}
			validationRules={validationRules}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(CustomerSelect);
